import jsPDF from 'jspdf';
import { format } from 'date-fns';

interface InvoiceItem {
  service_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  original_price?: number;
  discount?: number;
}

interface InvoiceSale {
  invoice_number: string | null;
  created_at: string;
  customer_name: string | null;
  customer_phone: string | null;
  total_amount: number;
  total_discount?: number;
  received_amount: number;
  due_amount: number;
  payment_mode: string;
  payment_status: string;
  notes?: string | null;
}

interface InvoiceTenant {
  name: string;
  address?: string | null;
  phone?: string | null;
}

interface InvoiceSettings {
  invoice_footer?: string | null;
  gst_enabled?: boolean;
  apply_gst_on_invoices?: boolean;
  cgst_percentage?: number;
  sgst_percentage?: number;
}

export function generateThermalInvoicePDF(
  sale: InvoiceSale,
  items: InvoiceItem[],
  tenant: InvoiceTenant,
  settings: InvoiceSettings = {}
): jsPDF {
  // Thermal receipt: 80mm width, dynamic height
  const receiptWidth = 80; // mm
  const margin = 4;
  const contentWidth = receiptWidth - (margin * 2);
  
  // Calculate content height dynamically
  const headerHeight = 30;
  const itemsHeight = items.length * 10 + 15;
  const totalsHeight = 45; // Increased for GST rows
  const footerHeight = 20;
  const receiptHeight = headerHeight + itemsHeight + totalsHeight + footerHeight + 30;

  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: [receiptWidth, Math.max(receiptHeight, 100)],
  });

  // Use Rs. instead of ₹ for PDF compatibility
  const formatCurrency = (amount: number) => `Rs.${Number(amount).toFixed(2)}`;
  
  let yPos = margin + 2;
  const centerX = receiptWidth / 2;

  // ===== HEADER =====
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text(tenant.name || 'VLE Plus', centerX, yPos, { align: 'center' });
  yPos += 5;

  if (tenant.address) {
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    const addressLines = doc.splitTextToSize(tenant.address, contentWidth);
    doc.text(addressLines, centerX, yPos, { align: 'center' });
    yPos += addressLines.length * 3;
  }

  if (tenant.phone) {
    doc.setFontSize(7);
    doc.text(`Ph: ${tenant.phone}`, centerX, yPos, { align: 'center' });
    yPos += 4;
  }

  // Dashed line
  doc.setLineDashPattern([1, 1], 0);
  doc.setLineWidth(0.2);
  doc.line(margin, yPos, receiptWidth - margin, yPos);
  yPos += 4;

  // Invoice title
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('BILL / INVOICE', centerX, yPos, { align: 'center' });
  yPos += 5;

  // Invoice details
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text(`No: ${sale.invoice_number || 'N/A'}`, margin, yPos);
  doc.text(format(new Date(sale.created_at), 'dd/MM/yy HH:mm'), receiptWidth - margin, yPos, { align: 'right' });
  yPos += 4;

  // Customer
  if (sale.customer_name) {
    doc.text(`Customer: ${sale.customer_name}`, margin, yPos);
    yPos += 3;
  }
  if (sale.customer_phone) {
    doc.text(`Ph: ${sale.customer_phone}`, margin, yPos);
    yPos += 3;
  }

  // Dashed line
  yPos += 2;
  doc.line(margin, yPos, receiptWidth - margin, yPos);
  yPos += 3;

  // ===== ITEMS =====
  // Header row
  doc.setFontSize(7);
  doc.setFont('helvetica', 'bold');
  doc.text('Item', margin, yPos);
  doc.text('Qty', margin + 35, yPos, { align: 'center' });
  doc.text('Disc', margin + 50, yPos, { align: 'right' });
  doc.text('Amt', receiptWidth - margin, yPos, { align: 'right' });
  yPos += 3;

  doc.setLineDashPattern([0.5, 0.5], 0);
  doc.line(margin, yPos, receiptWidth - margin, yPos);
  yPos += 3;

  // Items
  let subtotalBeforeDiscount = 0;
  let itemLevelDiscount = 0;
  
  doc.setFont('helvetica', 'normal');
  items.forEach((item) => {
    const itemBaseAmount = item.unit_price * item.quantity;
    const itemDiscount = item.discount || 0;
    
    subtotalBeforeDiscount += itemBaseAmount;
    itemLevelDiscount += itemDiscount;
    
    // Truncate long names
    let itemName = item.service_name;
    if (itemName.length > 16) {
      itemName = itemName.substring(0, 14) + '..';
    }
    
    doc.text(itemName, margin, yPos);
    doc.text(item.quantity.toString(), margin + 35, yPos, { align: 'center' });
    doc.text(itemDiscount > 0 ? `-${itemDiscount}` : '-', margin + 50, yPos, { align: 'right' });
    doc.text(formatCurrency(item.total_price), receiptWidth - margin, yPos, { align: 'right' });
    yPos += 4;
  });

  // Use sale-level total_discount if item-level discounts are 0
  const totalDiscount = itemLevelDiscount > 0 ? itemLevelDiscount : (sale.total_discount || 0);

  // Dashed line
  yPos += 1;
  doc.setLineDashPattern([1, 1], 0);
  doc.line(margin, yPos, receiptWidth - margin, yPos);
  yPos += 4;

  // ===== TOTALS =====
  doc.setFontSize(8);
  
  // Calculate GST if enabled
  const applyGst = settings.gst_enabled && settings.apply_gst_on_invoices;
  const cgstPercent = settings.cgst_percentage || 0;
  const sgstPercent = settings.sgst_percentage || 0;
  
  // Amount after discount (taxable amount)
  const amountAfterDiscount = subtotalBeforeDiscount - totalDiscount;
  
  // GST amounts
  const cgstAmount = applyGst ? (amountAfterDiscount * cgstPercent) / 100 : 0;
  const sgstAmount = applyGst ? (amountAfterDiscount * sgstPercent) / 100 : 0;
  const totalGst = cgstAmount + sgstAmount;

  // Subtotal (before discount)
  doc.text('Subtotal:', margin, yPos);
  doc.text(formatCurrency(subtotalBeforeDiscount), receiptWidth - margin, yPos, { align: 'right' });
  yPos += 4;

  // Total Discount (if any)
  if (totalDiscount > 0) {
    doc.text('Discount:', margin, yPos);
    doc.text(`-${formatCurrency(totalDiscount)}`, receiptWidth - margin, yPos, { align: 'right' });
    yPos += 4;
  }

  // GST Breakdown (if enabled)
  if (applyGst && totalGst > 0) {
    doc.setFontSize(7);
    doc.text(`CGST (${cgstPercent}%):`, margin, yPos);
    doc.text(formatCurrency(cgstAmount), receiptWidth - margin, yPos, { align: 'right' });
    yPos += 3;
    
    doc.text(`SGST (${sgstPercent}%):`, margin, yPos);
    doc.text(formatCurrency(sgstAmount), receiptWidth - margin, yPos, { align: 'right' });
    yPos += 4;
    doc.setFontSize(8);
  }

  // Received
  doc.text('Paid:', margin, yPos);
  doc.text(formatCurrency(sale.received_amount), receiptWidth - margin, yPos, { align: 'right' });
  yPos += 4;

  // Due (if any)
  if (sale.due_amount > 0) {
    doc.setFont('helvetica', 'bold');
    doc.text('DUE:', margin, yPos);
    doc.text(formatCurrency(sale.due_amount), receiptWidth - margin, yPos, { align: 'right' });
    yPos += 4;
  }

  // Double line before grand total
  doc.setLineDashPattern([], 0);
  doc.setLineWidth(0.3);
  doc.line(margin, yPos, receiptWidth - margin, yPos);
  yPos += 1;
  doc.line(margin, yPos, receiptWidth - margin, yPos);
  yPos += 4;

  // Grand Total
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('TOTAL:', margin, yPos);
  doc.text(formatCurrency(sale.total_amount), receiptWidth - margin, yPos, { align: 'right' });
  yPos += 6;

  // Payment mode
  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  doc.text(`Payment: ${sale.payment_mode?.toUpperCase() || 'CASH'}`, margin, yPos);
  
  const statusColor = sale.payment_status === 'paid' ? 'PAID' : sale.payment_status === 'partial' ? 'PARTIAL' : 'UNPAID';
  doc.text(statusColor, receiptWidth - margin, yPos, { align: 'right' });
  yPos += 5;

  // Dashed line
  doc.setLineDashPattern([1, 1], 0);
  doc.line(margin, yPos, receiptWidth - margin, yPos);
  yPos += 4;

  // ===== FOOTER =====
  doc.setFontSize(6);
  doc.setFont('helvetica', 'italic');
  const footer = settings.invoice_footer || 'Thank you for your business!';
  doc.text(footer, centerX, yPos, { align: 'center' });
  yPos += 3;
  doc.text('Powered by VLE Plus', centerX, yPos, { align: 'center' });

  return doc;
}

export function downloadThermalInvoicePDF(
  sale: InvoiceSale,
  items: InvoiceItem[],
  tenant: InvoiceTenant,
  settings: InvoiceSettings = {}
): void {
  const doc = generateThermalInvoicePDF(sale, items, tenant, settings);
  const fileName = `Receipt-${sale.invoice_number || 'DRAFT'}_${format(new Date(sale.created_at), 'yyyyMMdd')}.pdf`;
  doc.save(fileName);
}

export function printThermalInvoicePDF(
  sale: InvoiceSale,
  items: InvoiceItem[],
  tenant: InvoiceTenant,
  settings: InvoiceSettings = {}
): void {
  const doc = generateThermalInvoicePDF(sale, items, tenant, settings);
  const pdfBlob = doc.output('blob');
  const pdfUrl = URL.createObjectURL(pdfBlob);
  const printWindow = window.open(pdfUrl, '_blank');
  
  if (printWindow) {
    printWindow.addEventListener('load', () => {
      printWindow.print();
    });
  }
}
